/**
 * PitchDeckShare — Public investor-grade landing page.
 *
 * Unauthenticated route /share/:token. Renders a premium scroll-driven
 * storytelling page : spatial anchor → geometric rigour → 3D magic.
 * Outputs a strict <meta name="robots" content="noindex,nofollow">.
 */
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import {
  Loader2, MapPin, ShieldCheck, Sparkles, ArrowRight, Mail,
  Compass, Box, Ruler, AlertCircle,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CONTACT_HREF = 'mailto:contact@peteruniverse.com';
const SITE_HREF = 'https://peteruniverse.com';

const PitchDeckShare = () => {
  const { token } = useParams();
  const [deck, setDeck] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Strict no-index, lock the page chrome.
  useEffect(() => {
    document.title = 'Peter\'s Universe — Aperçu projet';
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow, noarchive, nosnippet, noimageindex';
    document.head.appendChild(meta);
    // Hide any inherited app chrome (alpha banners, etc.) just in case.
    document.body.classList.add('pitch-deck-public');
    // Strip the Google Maps "alpha" banner from the top of the page on this
    // public route — we never instantiate a <Map>, only consume static PNGs.
    const hideStyle = document.createElement('style');
    hideStyle.textContent = `
      body.pitch-deck-public > .gmnoprint,
      body.pitch-deck-public > [class*="gm-style"],
      body.pitch-deck-public > div[style*="position: fixed"][style*="top: 0"]:has(button) {
        display: none !important;
      }
      /* Google's dev-key alpha warning banner */
      body.pitch-deck-public div.gm-err-container,
      body.pitch-deck-public iframe[src*="googleapis.com"][style*="top: 0"],
      body.pitch-deck-public > div[role="alert"] {
        display: none !important;
      }
    `;
    document.head.appendChild(hideStyle);
    return () => {
      meta.remove();
      hideStyle.remove();
      document.body.classList.remove('pitch-deck-public');
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/share/${token}`)
      .then((r) => { if (!cancelled) setDeck(r.data); })
      .catch((e) => {
        if (!cancelled) setError(e?.response?.data?.detail || 'Lien invalide ou expiré');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [token]);

  const assetUrl = useMemo(() => {
    if (!deck) return () => null;
    return (kind) => deck.asset_urls?.[kind]
      ? `${process.env.REACT_APP_BACKEND_URL}${deck.asset_urls[kind]}`
      : null;
  }, [deck]);

  if (loading) {
    return (
      <div style={pageStyle.shell}>
        <div style={pageStyle.centered}>
          <Loader2 size={28} style={{ color: '#D9A05B' }} className="animate-spin" />
          <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: 13, marginTop: 12 }}>
            Chargement de votre aperçu sécurisé…
          </p>
        </div>
      </div>
    );
  }

  if (error || !deck) {
    return (
      <div style={pageStyle.shell}>
        <div style={pageStyle.centered}>
          <AlertCircle size={28} style={{ color: '#FCA5A5' }} />
          <h1 style={{ color: '#fff', fontSize: 22, margin: '14px 0 8px', fontFamily: 'Outfit, sans-serif' }}>
            Lien inaccessible
          </h1>
          <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: 13, maxWidth: 380, textAlign: 'center' }}>
            {error || 'Ce lien de partage est invalide, expiré ou a été révoqué.'}
          </p>
          <a href={SITE_HREF} style={{ ...pageStyle.cta, marginTop: 24 }}>
            Découvrir Peter's Universe <ArrowRight size={14} />
          </a>
        </div>
      </div>
    );
  }

  const { project, metrics, cadastre_meta, geo, style, render } = deck;
  const has = deck.assets_available || {};

  return (
    <div style={pageStyle.shell} data-testid="pitch-deck-shell">
      {/* ===== Discreet header ===== */}
      <header style={pageStyle.header} data-testid="pitch-deck-header">
        <a href={SITE_HREF} target="_blank" rel="noreferrer" style={pageStyle.brand}>
          <span style={{ color: '#D9A05B', fontFamily: 'Outfit, sans-serif', fontWeight: 700, letterSpacing: 1.5 }}>
            PETER'S
          </span>
          <span style={{ color: 'rgba(255,255,255,0.6)', fontFamily: 'Outfit, sans-serif', fontWeight: 400, marginLeft: 6, letterSpacing: 1.5 }}>
            UNIVERSE
          </span>
        </a>
        <a href={CONTACT_HREF} style={pageStyle.cta} data-testid="pitch-deck-cta-top">
          <Mail size={13} />
          Prendre contact
        </a>
      </header>

      {/* ===== HERO ===== */}
      <section style={pageStyle.hero} data-testid="pitch-deck-hero">
        <div style={pageStyle.heroLeft}>
          <span style={pageStyle.eyebrow}>Étude de cas — Pipeline géométrique propriétaire</span>
          <h1 style={pageStyle.h1}>{project.name || 'Projet immobilier'}</h1>
          {project.address && (
            <p style={pageStyle.heroAddress}>
              <MapPin size={14} style={{ verticalAlign: '-2px', marginRight: 6, color: '#D9A05B' }} />
              {project.address}
            </p>
          )}
          <div style={pageStyle.heroMetrics} data-testid="pitch-deck-metrics">
            <Metric value={metrics.node_count} label="nœuds géolocalisés" />
            <Metric value={metrics.edge_count} label="murs vectorisés" />
            <Metric value={`${(metrics.total_length_m || 0).toFixed(1)} m`} label="périmètre validé" />
          </div>
        </div>
        {has.render && (
          <div style={pageStyle.heroRight} data-testid="pitch-deck-hero-render">
            <img
              src={assetUrl('render')}
              alt="Premier rendu 3D"
              style={pageStyle.heroImg}
            />
            <div style={pageStyle.heroFloatBadge}>
              <Sparkles size={11} />
              Rendu IA contraint par notre géométrie
            </div>
          </div>
        )}
      </section>

      {/* ===== SECTION 1 : SPATIAL ANCHOR ===== */}
      {has.cadastre && (
        <section style={pageStyle.section} data-testid="pitch-deck-section-anchor">
          <ScrollEyebrow icon={Compass} label="01 — L'ancrage spatial" />
          <h2 style={pageStyle.h2}>
            Chaque projet commence par une parcelle réelle.
          </h2>
          <p style={pageStyle.lede}>
            Nous récupérons la géométrie cadastrale officielle depuis l'IGN et l'incrustons
            sur la vue satellite. Aucune approximation, aucune projection inventée.
          </p>
          <div style={pageStyle.frame}>
            <img
              src={assetUrl('cadastre')}
              alt="Parcelle cadastrale sur vue satellite"
              style={pageStyle.frameImg}
            />
          </div>
          <div style={pageStyle.specGrid}>
            <Spec label="Coordonnées centre"
                  value={cadastre_meta.center
                    ? `${cadastre_meta.center[0].toFixed(5)}, ${cadastre_meta.center[1].toFixed(5)}`
                    : '—'} />
            <Spec label="Zoom satellite" value={cadastre_meta.zoom ?? '—'} />
            <Spec label="Sommets parcelle" value={cadastre_meta.polygon_vertices} />
            <Spec label="Source géométrique" value="IGN apicarto" />
          </div>
        </section>
      )}

      {/* ===== SECTION 2 : GEOMETRIC RIGOUR — BEFORE/AFTER ===== */}
      <section style={pageStyle.section} data-testid="pitch-deck-section-rigor">
        <ScrollEyebrow icon={Ruler} label="02 — La rigueur géométrique" />
        <h2 style={pageStyle.h2}>
          Du plan scanné au masque vectoriel — sans aucune hallucination.
        </h2>
        <p style={pageStyle.lede}>
          Notre moteur OpenCV déterministe transforme un plan d'architecte en
          un graphe de murs strict (calibration métrique sub-pixel),
          puis en un masque <strong style={{ color: '#fff' }}>noir &amp; blanc</strong> qui
          conditionne l'IA comme un signal ControlNet absolu.
        </p>

        <div style={pageStyle.beforeAfter}>
          <div style={pageStyle.beforeAfterPane} data-testid="pitch-deck-before">
            <div style={pageStyle.paneLabel}>
              <span style={pageStyle.paneBadge}>AVANT</span>
              Plan source scanné
            </div>
            <div style={pageStyle.paneFrame}>
              {has.plan ? (
                <img
                  src={assetUrl('plan')}
                  alt="Plan d'architecte source"
                  style={pageStyle.paneImg}
                />
              ) : (
                <PaneEmpty text="Plan source non joint à cet aperçu" />
              )}
            </div>
          </div>
          <div style={pageStyle.beforeAfterArrow}>
            <ArrowRight size={28} style={{ color: 'var(--c-gold, #D9A05B)' }} />
          </div>
          <div style={pageStyle.beforeAfterPane} data-testid="pitch-deck-after">
            <div style={pageStyle.paneLabel}>
              <span style={{ ...pageStyle.paneBadge, background: 'rgba(34,211,238,0.12)', color: '#22D3EE', borderColor: 'rgba(34,211,238,0.35)' }}>
                APRÈS
              </span>
              Masque ControlNet validé
            </div>
            <div style={{ ...pageStyle.paneFrame, background: '#000' }}>
              {has.mask ? (
                <img
                  src={assetUrl('mask')}
                  alt="Masque ControlNet B&W"
                  style={{ ...pageStyle.paneImg, imageRendering: 'pixelated' }}
                />
              ) : (
                <PaneEmpty text="Masque non disponible" />
              )}
            </div>
          </div>
        </div>

        <div style={pageStyle.specGrid}>
          <Spec label="Méthode d'extraction" value="OpenCV déterministe" />
          <Spec label="Calibration métrique" value="2 points + échelle exacte" />
          <Spec label="Ancrage géospatial" value={geo?.node_count ? `${geo.node_count} nœuds lat/lng` : '—'} />
          <Spec label="Premier nœud GPS"
                value={geo?.first_node
                  ? `${geo.first_node.lat.toFixed(5)}, ${geo.first_node.lng.toFixed(5)}`
                  : '—'} />
        </div>
      </section>

      {/* ===== SECTION 3 : THE MAGIC ===== */}
      {has.render && (
        <section style={pageStyle.sectionDark} data-testid="pitch-deck-section-magic">
          <ScrollEyebrow icon={Box} label="03 — La magie" tone="gold" />
          <h2 style={pageStyle.h2Hero}>
            La géométrie est sacrée. <br />
            <span style={{ color: '#D9A05B' }}>Le style est libre.</span>
          </h2>
          <p style={pageStyle.ledeHero}>
            Le masque verrouille l'IA sur nos murs. Les références visuelles du projet
            (matières, couleurs, ambiances) guident la composition photo-réaliste.
            Voici le premier rendu — généré en moins de 30 secondes.
          </p>

          <div style={pageStyle.heroRenderFrame} data-testid="pitch-deck-render-hero">
            <img
              src={assetUrl('render')}
              alt="Premier rendu 3D photoréaliste"
              style={pageStyle.heroRenderImg}
            />
            {render?.model && (
              <div style={pageStyle.renderCaption}>
                <Sparkles size={11} style={{ color: '#D9A05B' }} />
                Premier rendu · {render.model}
                {render.generated_at && (
                  <span style={{ opacity: 0.5 }}>
                    · {new Date(render.generated_at).toLocaleDateString('fr-FR')}
                  </span>
                )}
              </div>
            )}
          </div>

          {(style?.tags?.length || style?.materials?.length) > 0 && (
            <div style={pageStyle.styleBlock} data-testid="pitch-deck-style">
              <div style={pageStyle.styleHeading}>
                <span style={pageStyle.eyebrow}>Intentions de style (CCTP)</span>
              </div>
              <div style={pageStyle.chips}>
                {(style.tags || []).map((t) => (
                  <span key={`tag-${t}`} style={pageStyle.chipGold} data-testid="pitch-deck-chip">
                    {t}
                  </span>
                ))}
                {(style.materials || []).map((m) => (
                  <span key={`mat-${m}`} style={pageStyle.chipCyan}>{m}</span>
                ))}
                {style.ambiance && (
                  <span style={pageStyle.chipAmbiance}>
                    <Sparkles size={10} /> {style.ambiance}
                  </span>
                )}
              </div>
            </div>
          )}
        </section>
      )}

      {/* ===== FOOTER CTA ===== */}
      <section style={pageStyle.footerCta} data-testid="pitch-deck-footer-cta">
        <h3 style={{ ...pageStyle.h2, marginBottom: 12, fontSize: 28 }}>
          Faites de votre prochain bien <br />
          <span style={{ color: '#D9A05B' }}>une démonstration de précision.</span>
        </h3>
        <p style={pageStyle.lede}>
          Peter's Universe transforme la rigueur géométrique en argument commercial vivant.
        </p>
        <div style={pageStyle.footerActions}>
          <a href={CONTACT_HREF} style={pageStyle.ctaBig} data-testid="pitch-deck-cta-contact">
            <Mail size={14} />
            Prendre contact
          </a>
          <a href={SITE_HREF} target="_blank" rel="noreferrer" style={pageStyle.ctaGhost}>
            Découvrir notre studio
            <ArrowRight size={13} />
          </a>
        </div>
      </section>

      <footer style={pageStyle.footer}>
        <ShieldCheck size={11} style={{ color: '#86EFAC', verticalAlign: '-2px', marginRight: 6 }} />
        Aperçu privé — lien signé · expire automatiquement · ne pas partager publiquement.
        {deck.validated_at && (
          <span style={{ marginLeft: 12, opacity: 0.5 }}>
            Géométrie validée le {new Date(deck.validated_at).toLocaleDateString('fr-FR')}
          </span>
        )}
      </footer>
    </div>
  );
};

// ===== Sub-components =====

const Metric = ({ value, label }) => (
  <div style={pageStyle.metric} data-testid="pitch-deck-metric">
    <div style={pageStyle.metricValue}>{value}</div>
    <div style={pageStyle.metricLabel}>{label}</div>
  </div>
);

const Spec = ({ label, value }) => (
  <div style={pageStyle.spec}>
    <div style={pageStyle.specLabel}>{label}</div>
    <div style={pageStyle.specValue}>{value}</div>
  </div>
);

const ScrollEyebrow = ({ icon: Icon, label, tone }) => (
  <div style={{ ...pageStyle.scrollEyebrow, color: tone === 'gold' ? '#D9A05B' : 'rgba(255,255,255,0.55)' }}>
    <Icon size={14} />
    <span>{label}</span>
  </div>
);

const PaneEmpty = ({ text }) => (
  <div style={{
    width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: 'rgba(255,255,255,0.4)', fontSize: 12, padding: 24, textAlign: 'center',
  }}>
    {text}
  </div>
);

// ===== Styles =====

const pageStyle = {
  shell: {
    minHeight: '100vh',
    background: '#0A0A0C',
    color: '#fff',
    fontFamily: 'Inter, system-ui, sans-serif',
    paddingBottom: 0,
  },
  centered: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  header: {
    position: 'fixed', top: 0, left: 0, right: 0, zIndex: 50,
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '14px 32px',
    background: 'rgba(10,10,12,0.85)',
    backdropFilter: 'blur(14px)',
    borderBottom: '1px solid rgba(255,255,255,0.05)',
  },
  brand: { textDecoration: 'none', fontSize: 13, letterSpacing: 1 },
  cta: {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '7px 14px', borderRadius: 6,
    background: 'rgba(217,160,91,0.10)', border: '1px solid rgba(217,160,91,0.35)',
    color: '#D9A05B', fontSize: 12, fontWeight: 500, textDecoration: 'none',
    transition: 'all 0.2s ease',
  },
  ctaBig: {
    display: 'inline-flex', alignItems: 'center', gap: 8,
    padding: '14px 24px', borderRadius: 10,
    background: 'linear-gradient(135deg, #D9A05B 0%, #B07A35 100%)',
    color: '#0E0E10', fontSize: 14, fontWeight: 600, textDecoration: 'none',
    fontFamily: 'Outfit, sans-serif',
    boxShadow: '0 6px 24px -8px rgba(217,160,91,0.50)',
  },
  ctaGhost: {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '14px 22px', borderRadius: 10,
    color: 'rgba(255,255,255,0.75)', fontSize: 13, fontWeight: 500, textDecoration: 'none',
    border: '1px solid rgba(255,255,255,0.18)',
    fontFamily: 'Outfit, sans-serif',
  },
  hero: {
    paddingTop: 120, paddingBottom: 80,
    paddingLeft: 48, paddingRight: 48,
    display: 'grid', gridTemplateColumns: 'minmax(280px, 1fr) 1.2fr', gap: 56,
    maxWidth: 1280, margin: '0 auto',
  },
  heroLeft: { display: 'flex', flexDirection: 'column', justifyContent: 'center' },
  heroRight: { position: 'relative' },
  eyebrow: {
    display: 'inline-block', fontSize: 11, letterSpacing: 2.4, textTransform: 'uppercase',
    color: '#D9A05B', fontWeight: 600, marginBottom: 18,
  },
  h1: {
    fontSize: 'clamp(36px, 5vw, 64px)', lineHeight: 1.05, fontWeight: 700,
    fontFamily: 'Outfit, sans-serif', margin: 0, letterSpacing: '-0.02em',
  },
  heroAddress: { color: 'rgba(255,255,255,0.6)', fontSize: 14, marginTop: 18, lineHeight: 1.5 },
  heroMetrics: { display: 'flex', gap: 32, marginTop: 32, flexWrap: 'wrap' },
  metric: { display: 'flex', flexDirection: 'column', gap: 4 },
  metricValue: {
    fontSize: 26, fontWeight: 700, fontFamily: 'Outfit, sans-serif',
    color: '#fff', letterSpacing: '-0.01em',
  },
  metricLabel: {
    fontSize: 10.5, color: 'rgba(255,255,255,0.55)', letterSpacing: 1, textTransform: 'uppercase',
  },
  heroImg: {
    width: '100%', borderRadius: 14, display: 'block',
    boxShadow: '0 30px 90px -30px rgba(217,160,91,0.40), 0 0 0 1px rgba(255,255,255,0.06)',
  },
  heroFloatBadge: {
    position: 'absolute', bottom: 20, left: 20,
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '8px 14px', borderRadius: 50,
    background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(10px)',
    color: '#D9A05B', fontSize: 11, fontWeight: 500,
    border: '1px solid rgba(217,160,91,0.35)',
  },
  section: {
    maxWidth: 1280, margin: '0 auto', padding: '80px 48px',
    borderTop: '1px solid rgba(255,255,255,0.06)',
  },
  sectionDark: {
    maxWidth: '100%', padding: '120px 48px',
    background: 'radial-gradient(ellipse at center, rgba(217,160,91,0.06) 0%, transparent 60%)',
    borderTop: '1px solid rgba(255,255,255,0.06)',
    textAlign: 'center',
  },
  scrollEyebrow: {
    display: 'inline-flex', alignItems: 'center', gap: 8,
    fontSize: 11, letterSpacing: 2, textTransform: 'uppercase',
    marginBottom: 18, fontWeight: 600,
  },
  h2: {
    fontSize: 'clamp(28px, 3.5vw, 44px)', lineHeight: 1.15, fontWeight: 700,
    fontFamily: 'Outfit, sans-serif', margin: 0, letterSpacing: '-0.02em',
    maxWidth: 760,
  },
  h2Hero: {
    fontSize: 'clamp(36px, 5vw, 60px)', lineHeight: 1.1, fontWeight: 700,
    fontFamily: 'Outfit, sans-serif', margin: '0 auto', letterSpacing: '-0.02em',
    maxWidth: 900, textAlign: 'center',
  },
  lede: {
    color: 'rgba(255,255,255,0.65)', fontSize: 15, lineHeight: 1.6, maxWidth: 700,
    marginTop: 20,
  },
  ledeHero: {
    color: 'rgba(255,255,255,0.65)', fontSize: 16, lineHeight: 1.65, maxWidth: 680,
    margin: '24px auto 56px', textAlign: 'center',
  },
  frame: {
    marginTop: 36, borderRadius: 14, overflow: 'hidden',
    boxShadow: '0 30px 80px -40px rgba(0,0,0,0.8), 0 0 0 1px rgba(255,255,255,0.06)',
  },
  frameImg: { width: '100%', display: 'block' },
  specGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 24,
    marginTop: 40,
  },
  spec: { display: 'flex', flexDirection: 'column', gap: 4 },
  specLabel: { fontSize: 10.5, color: 'rgba(255,255,255,0.45)', letterSpacing: 1.5, textTransform: 'uppercase' },
  specValue: { fontSize: 14, color: '#fff', fontFamily: 'Outfit, sans-serif', fontWeight: 500 },
  beforeAfter: {
    marginTop: 40, display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 24,
    alignItems: 'stretch',
  },
  beforeAfterPane: { display: 'flex', flexDirection: 'column', gap: 12 },
  beforeAfterArrow: { display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: 32 },
  paneLabel: {
    display: 'flex', alignItems: 'center', gap: 10,
    color: 'rgba(255,255,255,0.65)', fontSize: 12,
  },
  paneBadge: {
    padding: '3px 8px', borderRadius: 4, fontSize: 9.5, fontWeight: 600,
    letterSpacing: 1.2, textTransform: 'uppercase',
    background: 'rgba(217,160,91,0.12)', color: '#D9A05B',
    border: '1px solid rgba(217,160,91,0.35)',
  },
  paneFrame: {
    flex: 1, borderRadius: 12, overflow: 'hidden',
    background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)',
    minHeight: 260, display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  paneImg: { width: '100%', height: '100%', objectFit: 'contain', display: 'block' },
  heroRenderFrame: {
    position: 'relative', maxWidth: 1100, margin: '0 auto', borderRadius: 18, overflow: 'hidden',
    boxShadow: '0 50px 150px -40px rgba(217,160,91,0.30), 0 0 0 1px rgba(255,255,255,0.08)',
  },
  heroRenderImg: { width: '100%', display: 'block' },
  renderCaption: {
    position: 'absolute', bottom: 18, right: 18,
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '7px 14px', borderRadius: 6,
    background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(10px)',
    color: 'rgba(255,255,255,0.75)', fontSize: 10.5,
    border: '1px solid rgba(255,255,255,0.10)',
  },
  styleBlock: { maxWidth: 800, margin: '48px auto 0', textAlign: 'left' },
  styleHeading: { marginBottom: 14, textAlign: 'center' },
  chips: { display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center' },
  chipGold: {
    padding: '6px 12px', borderRadius: 50, fontSize: 12,
    background: 'rgba(217,160,91,0.10)', color: '#D9A05B',
    border: '1px solid rgba(217,160,91,0.30)',
  },
  chipCyan: {
    padding: '6px 12px', borderRadius: 50, fontSize: 12,
    background: 'rgba(34,211,238,0.08)', color: '#22D3EE',
    border: '1px solid rgba(34,211,238,0.25)',
  },
  chipAmbiance: {
    display: 'inline-flex', alignItems: 'center', gap: 5,
    padding: '6px 12px', borderRadius: 50, fontSize: 12,
    background: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.78)',
    border: '1px solid rgba(255,255,255,0.12)',
  },
  footerCta: {
    maxWidth: 900, margin: '0 auto', padding: '120px 48px 80px',
    textAlign: 'center',
  },
  footerActions: {
    display: 'flex', flexWrap: 'wrap', gap: 16, justifyContent: 'center', marginTop: 36,
  },
  footer: {
    borderTop: '1px solid rgba(255,255,255,0.05)',
    padding: '24px 48px', textAlign: 'center',
    color: 'rgba(255,255,255,0.45)', fontSize: 11,
  },
};

export default PitchDeckShare;
